/**
 * AuthContext - real Microsoft SSO via MSAL.
 *
 * The signed-in user is whoever MSAL says it is. We thinly wrap it so the rest
 * of the app keeps using { user, isAdmin, signIn, signOut } and never sees MSAL
 * directly.
 *
 * Requires a secure context (HTTPS) - runs at https://ops.globalat.com in prod.
 *
 * Domain guard: even though the Entra app is single-tenant (only @globalat.com
 * accounts can reach the popup), we re-check on the client as defense in depth
 * - guest accounts and shadow tenants have surprised people before.
 */
import { createContext, useContext, useCallback, useMemo, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/msalConfig';
import { isAdmin as checkAdmin } from '../data/admins';

const AuthContext = createContext(null);

const isAllowedDomain = (email) =>
  !!email && email.trim().toLowerCase().endsWith('@globalat.com');

export function AuthProvider({ children }) {
  const { instance, accounts } = useMsal();
  const account = accounts[0] || null;

  // Build the in-app user shape from the MSAL account.
  const user = account && isAllowedDomain(account.username)
    ? { email: account.username.toLowerCase(), name: account.name }
    : null;

  // Self-populating roster: whenever we have a signed-in user (fresh login OR
  // restored session), upsert them into the backend users registry so they
  // become selectable as an opportunity owner and their Entra display name is
  // captured. Fire-and-forget; a failure here must not block the app.
  useEffect(() => {
    if (!user) return;
    fetch('/api/users/me', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: user.email, name: user.name }),
    }).catch(() => { /* registry upsert is best-effort */ });
  }, [user?.email, user?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = useCallback(async () => {
    // Redirect flow: navigates the whole tab to Microsoft and back. The page
    // leaves, so code after this won't run on success; handleRedirectPromise()
    // in index.js completes sign-in when we return. Domain is enforced in the
    // `user` derivation above (a non-@globalat.com account yields user=null).
    try {
      await instance.loginRedirect(loginRequest);
      return { ok: true };
    } catch (e) {
      // 'interaction_in_progress' means an earlier redirect was abandoned and
      // left MSAL's interaction flag stuck - every sign-in then fails. Purge the
      // stale MSAL state and reload so the next click starts clean.
      if (e?.errorCode === 'interaction_in_progress') {
        try { await instance.clearCache(); } catch { /* best effort */ }
        try {
          Object.keys(window.localStorage)
            .filter((k) => k.startsWith('msal.'))
            .forEach((k) => window.localStorage.removeItem(k));
        } catch { /* ignore */ }
        window.location.reload();
        return { ok: false, error: 'Sign-in was busy - resetting. Click Sign in again.' };
      }
      return { ok: false, error: e?.errorMessage || e?.message || 'Sign-in failed.' };
    }
  }, [instance]);

  const signOut = useCallback(() => {
    instance.logoutRedirect({ account });
  }, [instance, account]);

  const value = useMemo(() => ({
    user,
    isAdmin: checkAdmin(user?.email),
    signIn,
    signOut,
  }), [user, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
